import Button from '../../../components/bootstrap/Button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../redux/reduxHooks';
import authAPI from '../../API/authAPI';
import { loginUser, resetUserError, setUserIsLoading } from '../../redux/reducers/authReducer';
import { useEffect } from 'react';
import Spinner from '../../../components/bootstrap/Spinner';
import { toast } from 'react-toastify';
import useDarkMode from '../../../hooks/useDarkMode';
import CustomTextInput from '../CustomTextInput';

interface LoginComponentProps {
	onForgotPassword?: () => void;
}

const LoginComponent = ({ onForgotPassword }: LoginComponentProps) => {
	/**
	 * useAppDispatch & useAppSelector are a hooks created from useDispatch and useSelector respectively
	 *
	 * - It is to avoid always stated the type of the store when making selects or dispatches
	 * - Check the redux toolkit documentation on integration with typescript for references
	 */
	const { darkModeStatus } = useDarkMode();
	const dispatch = useAppDispatch();
	const { error, isLoading } = useAppSelector((state) => state.auth);
	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			email: '',
			password: '',
		},
		validationSchema: Yup.object().shape({
			email: Yup.string().required('Email is required').email('Invalid email'),
			password: Yup.string().required('Password is required'),
		}),
		onSubmit: async (values) => {
			dispatch(setUserIsLoading());
			/**
			 * A separate custom directory called API is created on the src of the project
			 *
			 * - This folder contains different API request calls with axios to the backend
			 * - The reason for this is because writing async functions directly on the slice create problems unless you use type ignore
			 * - Instead we have separated the API calls from the store and now we only use the store to get the stages of the actions
			 * - Like isLoading, error, success, the data etc. and do the necessary renderings
			 * - We await all API calls because they promises(async functions)
			 */
			const data = await authAPI.login(values);
			dispatch(loginUser(data));
		},
	});

	useEffect(() => {
		if (error) {
			toast(error, {
				position: 'top-right',
				type: 'error',
				autoClose: 3000,
				theme: darkModeStatus ? 'dark' : 'light',
				onClose: () => dispatch(resetUserError()),
			});
		}

		return () => {
			dispatch(resetUserError());
		};
	}, [error]);
	return (
		<div className="login-form">
			<form onSubmit={formik.handleSubmit}>
				<div className='mb-3'>
					<CustomTextInput
						id='email'
						placeholder='Email Address'
						autoComplete='email'
						value={formik.values.email}
						isTouched={formik.touched.email}
						invalidFeedback={formik.errors.email}
						isValid={formik.isValid}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
					/>
				</div>
				
				<div className='mb-4'>
					<CustomTextInput
						id='password'
						placeholder='Password'
						type='password'
						autoComplete='current-password'
						value={formik.values.password}
						isTouched={formik.touched.password}
						invalidFeedback={formik.errors.password}
						isValid={formik.isValid}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
					/>
				</div>

				<div className='d-grid gap-2'>
					<Button
						type='submit'
						isDisable={isLoading || !formik.isValid}
						color='primary'
						className='py-3 fw-semibold'
					>
						{isLoading && <Spinner className='me-2' isSmall inButton isGrow />}
						Sign In
					</Button>
					
					{onForgotPassword && (
						<Button
							type='button'
							color='link'
							className='text-decoration-none p-2'
							onClick={onForgotPassword}
						>
							Forgot your password?
						</Button>
					)}
				</div>
			</form>
		</div>
	);
};

export default LoginComponent;
